/**
 * @file     RentEstimateCard.tsx
 * @purpose  "What will it rent for?" (design 10a to 10c): the address,
 *           bedrooms and home type give the asking band from listed rentals
 *           nearby; a VOW registrant also sees the leased band. The range
 *           uses MLS® data, so a signed-out visitor creates a free account
 *           first and the answers survive the round trip. "Rent out your
 *           place" then starts a draft carrying the address.
 */
'use client';

import '@/core/i18n/deferred/listings';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { useTranslation } from 'react-i18next';
import { AddressAutocomplete, type StructuredAddress } from '@/components/forms/AddressAutocomplete';
import { SegmentedControl } from '@/components/hub/SegmentedControl';
import { VowAccessModal } from '@/components/vow/VowAccessModal';
import { useAuth } from '@/hooks/useAuth';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { reportSilentFailure } from '@/core/observability/reportSilentFailure';
import { ListingApiError } from '../api';
import { OWNER_LISTING_EVENTS, useOwnerListingAnalytics } from '../analytics';
import { formatRent } from '../format';
import { FieldError, OwnerButton, hairline, hubColors, microLabelSx, radius } from '../ui';
import { useLeaseValue } from '../useLeaseValue';
import { addressFromSelection, type AddressParts } from '../steps/AddressStep';
import { builderHref, readEstimateMemory, startListing, writeEstimateMemory } from './startListing';

const BEDROOMS = ['0', '1', '2', '3', '4'] as const;
const HOME_TYPES = ['apartment_condo', 'townhouse', 'house'] as const;
export const ESTIMATE_ADDRESS_INPUT_ID = 'rent-estimate-address';

export function RentEstimateCard() {
  const { t } = useTranslation();
  const router = useRouter();
  const { isAuthenticated, isLoading } = useAuth();
  const { track, trackCtaClick } = useOwnerListingAnalytics();
  const { state, run } = useLeaseValue();
  const [query, setQuery] = useState('');
  const [address, setAddress] = useState<AddressParts | null>(null);
  const [addressError, setAddressError] = useState<'not_found' | 'outside_bc' | null>(null);
  const [bedrooms, setBedrooms] = useState<string>('2');
  const [homeType, setHomeType] = useState<string>('apartment_condo');
  const [accountOpen, setAccountOpen] = useState(false);
  const [vowOpen, setVowOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const started = useRef(false);
  const resumed = useRef(false);

  const markStarted = () => {
    if (started.current) return;
    started.current = true;
    track(OWNER_LISTING_EVENTS.rentEstimateStarted, {});
  };

  const estimate = async (input = { query, address, bedrooms, homeType }) => {
    markStarted();
    if (!input.address) return setAddressError('not_found');
    writeEstimateMemory({ query: input.query, address: input.address, bedrooms: Number(input.bedrooms), homeType: input.homeType });
    if (!isAuthenticated) return setAccountOpen(true);
    const result = await run({
      address: `${input.address.streetNumber} ${input.address.streetName}, ${input.address.city}, BC`,
      bedrooms: Number(input.bedrooms),
      homeType: input.homeType,
    });
    if (result.status === 'ready') {
      track(OWNER_LISTING_EVENTS.rentEstimateShown, {
        band_low: result.asking?.low ?? null,
        band_high: result.asking?.high ?? null,
        comps_count: result.asking?.count ?? 0,
        leased_shown: Boolean(result.leased),
      });
    }
  };

  // After the sign-up round trip, pick the answers back up and run once.
  useEffect(() => {
    if (resumed.current || isLoading) return;
    resumed.current = true;
    const memory = readEstimateMemory();
    if (!memory?.address) return;
    setQuery(memory.query);
    setAddress(memory.address);
    setBedrooms(String(Math.min(4, memory.bedrooms)));
    setHomeType(memory.homeType);
    if (isAuthenticated) void estimate({ query: memory.query, address: memory.address, bedrooms: String(memory.bedrooms), homeType: memory.homeType });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isAuthenticated]);

  const onSelect = (sel: StructuredAddress) => {
    markStarted();
    const parts = addressFromSelection(sel);
    if (parts === 'outside_bc') {
      setAddress(null);
      return setAddressError('outside_bc');
    }
    setAddress(parts);
    setAddressError(parts ? null : 'not_found');
  };

  const begin = async () => {
    trackCtaClick('rent_out_your_place', '/rent-out-your-place', '/dashboard/listings', { cta_id: 'rent_out_your_place', listing_type: 'lease', listing_source: 'owner' });
    if (!isAuthenticated) {
      writeEstimateMemory(address ? { query, address, bedrooms: Number(bedrooms), homeType } : null);
      return setAccountOpen(true);
    }
    setStarting(true);
    setStartError(null);
    try {
      const id = await startListing({ kind: 'lease', address, attributes: { bedrooms: Number(bedrooms), homeType } });
      track(OWNER_LISTING_EVENTS.draftCreated, { source: 'estimate' });
      writeEstimateMemory(null);
      router.push(builderHref(id, 'estimate'));
    } catch (err) {
      const limit = err instanceof ListingApiError && err.details.includes('drafts:limit');
      if (!limit) reportSilentFailure(err, { area: 'owner-listings/start-from-estimate' });
      setStartError(t(limit ? 'listings.owner.estimate.draftLimit' : 'listings.owner.estimate.startError'));
      setStarting(false);
    }
  };

  const ready = state.status === 'ready' ? state : null;

  return (
    <Box id="rent-estimate" component="section" aria-labelledby="rent-estimate-title" sx={{ backgroundColor: hubColors.surfaceCard, border: hairline, borderRadius: `${radius.lg}px`, p: { xs: 5, md: 6 }, display: 'flex', flexDirection: 'column', gap: 5 }}>
      <Box component="h2" id="rent-estimate-title" sx={{ m: 0, fontSize: 22, fontWeight: fontWeightTokens.semibold, letterSpacing: '-0.008em' }}>{t('listings.owner.estimate.title')}</Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <AddressAutocomplete
          value={query}
          onChange={(v) => {
            setQuery(v);
            setAddress(null);
            if (addressError) setAddressError(null);
          }}
          onSelect={onSelect}
          textFieldProps={{
            id: ESTIMATE_ADDRESS_INPUT_ID,
            label: t('listings.owner.estimate.address'),
            placeholder: t('listings.owner.estimate.addressPlaceholder'),
            error: Boolean(addressError) || state.status === 'not_found',
          }}
        />
        {addressError || state.status === 'not_found' ? (
          <FieldError>{t(addressError === 'outside_bc' ? 'listings.owner.address.outsideBc' : 'listings.owner.estimate.notFound')}</FieldError>
        ) : null}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box component="p" id="rent-estimate-beds" sx={microLabelSx}>{t('listings.owner.estimate.bedrooms')}</Box>
        <SegmentedControl
          labelledBy="rent-estimate-beds"
          ariaLabel={t('listings.owner.estimate.bedrooms')}
          fullWidth
          value={bedrooms}
          onChange={(v) => { markStarted(); setBedrooms(v); }}
          options={BEDROOMS.map((b) => ({ value: b, label: b === '0' ? t('listings.owner.estimate.studio') : b === '4' ? '4+' : b }))}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box component="p" id="rent-estimate-type" sx={microLabelSx}>{t('listings.owner.estimate.homeType')}</Box>
        <SegmentedControl
          labelledBy="rent-estimate-type"
          ariaLabel={t('listings.owner.estimate.homeType')}
          fullWidth
          value={homeType}
          onChange={(v) => { markStarted(); setHomeType(v); }}
          options={HOME_TYPES.map((h) => ({ value: h, label: t(`listings.owner.estimate.homeTypes.${h}`) }))}
        />
      </Box>

      {!ready ? (
        <OwnerButton onClick={() => void estimate()} disabled={state.status === 'loading'} aria-busy={state.status === 'loading' || undefined} fullWidth>
          {state.status === 'loading' ? t('listings.owner.estimate.loading') : t('listings.owner.estimate.submit')}
        </OwnerButton>
      ) : null}

      {state.status === 'loading' ? (
        <Box aria-hidden>
          <Skeleton variant="text" width="70%" height={36} />
          <Skeleton variant="text" width="90%" />
        </Box>
      ) : null}
      {state.status === 'error' ? <FieldError>{t('listings.owner.estimate.error')}</FieldError> : null}

      {ready ? (
        <Box role="status" sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 4, borderTop: hairline }}>
          {address ? (
            <Box component="p" sx={{ m: 0, fontSize: 13.5, color: hubColors.textMuted }}>
              {t('listings.owner.estimate.summary', {
                address: `${address.streetNumber} ${address.streetName}`,
                beds: bedrooms === '0' ? t('listings.owner.estimate.studio') : t('listings.owner.estimate.beds', { count: Number(bedrooms) }),
                type: t(`listings.owner.estimate.homeTypes.${homeType}`).toLowerCase(),
              })}
            </Box>
          ) : null}
          {ready.asking ? (
            <>
              <Box component="p" sx={{ m: 0, fontSize: { xs: 22, md: 26 }, fontWeight: fontWeightTokens.semibold, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
                {t('listings.owner.estimate.bandLine', { low: formatRent(ready.asking.low), high: formatRent(ready.asking.high) })}
              </Box>
              <Box component="p" sx={{ m: 0, fontSize: 13, color: hubColors.textMuted }}>
                {t('listings.owner.estimate.bandMethod', { count: ready.asking.count, radius: ready.asking.radiusKm })}
              </Box>
            </>
          ) : (
            <Box component="p" sx={{ m: 0, fontSize: 14.5, color: hubColors.textSecondary }}>{t('listings.owner.estimate.insufficient')}</Box>
          )}
          {ready.leased ? (
            <Box sx={{ p: 4, borderRadius: `${radius.md}px`, backgroundColor: hubColors.surfaceSunken }}>
              <Box component="p" sx={microLabelSx}>{t('listings.owner.estimate.leasedTitle')}</Box>
              <Box component="p" sx={{ m: 0, mt: 1, fontSize: 15, fontWeight: fontWeightTokens.medium, fontVariantNumeric: 'tabular-nums' }}>
                {t('listings.owner.estimate.leasedLine', { low: formatRent(ready.leased.low), high: formatRent(ready.leased.high), count: ready.leased.count, radius: ready.leased.radiusKm })}
              </Box>
              <Box component="p" sx={{ m: 0, mt: 1, fontSize: 12.5, color: hubColors.textMuted }}>{t('listings.owner.estimate.leasedTerms')}</Box>
            </Box>
          ) : null}
          <OwnerButton onClick={() => void begin()} disabled={starting} aria-busy={starting || undefined} fullWidth>
            {starting ? t('listings.owner.estimate.starting') : t('listings.owner.estimate.start')}
          </OwnerButton>
          {ready.leasedLocked ? (
            <OwnerButton tone="secondary" onClick={() => setVowOpen(true)} fullWidth>{t('listings.owner.estimate.seeLeased')}</OwnerButton>
          ) : null}
          {startError ? <FieldError>{startError}</FieldError> : null}
        </Box>
      ) : null}

      {!isAuthenticated && !isLoading && !ready ? (
        <Box component="p" sx={{ m: 0, mt: -2, fontSize: 13, color: hubColors.textMuted }}>{t('listings.owner.estimate.signedOutLine')}</Box>
      ) : null}

      <VowAccessModal
        open={accountOpen}
        onClose={() => setAccountOpen(false)}
        authed={false}
        redirectPath="/rent-out-your-place"
        eyebrow={t('listings.owner.door.eyebrow')}
        headline={t('listings.owner.estimate.signedOutTitle')}
      />
      <VowAccessModal
        open={vowOpen}
        onClose={() => setVowOpen(false)}
        authed
        redirectPath="/rent-out-your-place"
        eyebrow={t('listings.owner.estimate.vowEyebrow')}
        headline={t('listings.owner.estimate.vowHeadline')}
      />
    </Box>
  );
}
